<script setup>
  import {computed, inject, onMounted, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentFoldable from "@components/Foldable/Foldable.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentQrCode from "@components/QrCode.vue";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentSignPatientDocumentsDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
  });

  defineEmits(["open", "close"]);

  const privacy = inject("$privacy");

  const refDialog = ref(null);
  const copied = ref(false);
  const internalPatient = ref(null);

  onMounted(async () => {
    internalPatient.value = await privacy.decryptPatient(props.patient);
  });

  const url = computed(() => {
    const fullName = (internalPatient.value?.firstname + " " + internalPatient.value?.lastname).trim();
    const base64Name = btoa(
      new TextEncoder().encode(fullName).reduce((data, byte) => data + String.fromCodePoint(byte), ""),
    ).replace(/=+$/, ""); // remove "=" padding from b64 string

    return (
      route("sign-patient-documents.show", {patientId: props.patient.id}) + `?name=${encodeURIComponent(base64Name)}`
    );
  });

  const copyToClipboard = () => {
    try {
      navigator.clipboard.writeText(url.value);
      copied.value = true;
      setTimeout(() => (copied.value = false), 1000);
    } catch (error) {
      console.warn("Failed to copy to clipboard:", error);
    }
  };

  function open() {
    refDialog.value.open();
  }

  function close() {
    refDialog.value.close();
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-title="Schnell und einfach digital signieren"
    dialog-type="information"
    primary-label="Schließen"
    dialog-width="large"
  >
    <template #content>
      <ComponentFoldable :open="true" title="Wie funktioniert das?" :data-test="getTestId('foldable-help')">
        <ol class="mt-2 flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2 lg:space-x-4">
          <li class="flex-1 rounded-md border-1 p-4 shadow-md md:p-2 md:pb-4 lg:p-4">
            <ComponentIcon custom="scan-qr-code" class="mx-auto" />
            <h3 class="text-xl font-semibold">1. QR-Code mit Mobilgerät scannen</h3>

            <p class="mt-4">
              Scannen Sie den untenstehenden QR-Code mit einem Tablet oder dem Smartphone des Patienten oder schicken
              Sie den Link per E-Mail.
            </p>
          </li>

          <li class="flex-1 rounded-md border-1 p-4 shadow-md md:p-2 md:pb-4 lg:p-4">
            <component-icon custom="options-on-mobile" class="mx-auto" />
            <h3 class="text-xl font-semibold">2. Einwilligungen zum Signieren auswählen</h3>
            <p class="mt-4">Ihr Patient kann entscheiden, welche Einwilligungen abgegeben werden.</p>
          </li>

          <li class="flex-1 rounded-md border-1 p-4 shadow-md md:p-2 md:pb-4 lg:p-4">
            <component-icon custom="sign-on-mobile" class="mx-auto" />
            <h3 class="text-xl font-semibold">3. Signieren und fertig!</h3>

            <p class="mt-4">
              Die Dokumente können direkt mit dem Finger auf dem Touchscreen signiert werden. Auf Wunsch wird eine Kopie
              an eine E-Mail Adresse versendet.
            </p>
          </li>
        </ol>
      </ComponentFoldable>

      <div class="mt-4 flex items-center justify-center space-x-4">
        <ComponentQrCode :value="url" />

        <div class="flex flex-col justify-end gap-4">
          <MedButton
            :variant="copied ? 'secondary' : 'default'"
            :icon="copied ? 'check' : 'content_copy'"
            :icon-filled="false"
            align="left"
            :data-test="getTestId('copy-sign-documents-link-to-clipboard-button')"
            @click="copyToClipboard()"
          >
            Link kopieren
          </MedButton>

          <MedButton
            as="a"
            :href="url"
            align="left"
            target="_blank"
            :data-test="getTestId('open-sign-documents-button')"
            icon="launch"
          >
            Link öffnen
          </MedButton>
        </div>
      </div>
    </template>
  </ComponentDialog>
</template>
